import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TenantIdentityConfigurationResponse, TenantIdentityConfigurationService, UpdateTenantIdentityConfigurationRequest } from '../../../shared/services/tenant-identity-configuration.service';

@Component({
  selector: 'app-integration-editor',
  templateUrl: './integration-editor.component.html',
  styleUrls: ['./integration-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule]
})
export class IntegrationEditorComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly tenantIdentityService = inject(TenantIdentityConfigurationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly statusMessage = signal('');
  readonly errorMessage = signal('');
  readonly hasClientSecret = signal(false);
  readonly lastUpdatedAt = signal<string | null>(null);

  readonly integrationForm: FormGroup = this.formBuilder.group({
    integrationBaseUrl: ['', [Validators.required, Validators.pattern(/^https:\/\/.+/i)]],
    clientId: ['', [Validators.required, Validators.pattern(/^[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/)]],
    clientSecret: [''],
    azureAdTenantId: ['', [Validators.pattern(/^[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/)]],
    callbackEndpointPath: ['']
  });

  constructor() {
    this.loadConfiguration();
  }

  loadConfiguration(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('');

    this.tenantIdentityService.getConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (config) => this.patchFormWithConfiguration(config),
        error: (error: Error) => {
          const message = error.message ?? 'Failed to load integration configuration.';
          if (message.toLowerCase().includes('not found')) {
            this.integrationForm.reset({
              integrationBaseUrl: '',
              clientId: '',
              clientSecret: '',
              azureAdTenantId: '',
              callbackEndpointPath: ''
            });
            this.hasClientSecret.set(false);
            this.lastUpdatedAt.set(null);
            this.statusMessage.set('No identity configuration found. Please enter new values.');
            this.errorMessage.set('');
          } else {
            this.errorMessage.set(message);
            this.statusMessage.set('');
          }
          this.isLoading.set(false);
        }
      });
  }

  async saveConfiguration(): Promise<void> {
    if (this.isSaving()) {
      return;
    }

    if (this.integrationForm.invalid) {
      this.integrationForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Saving integration settings...');

    const formValue = this.integrationForm.getRawValue();
    const request: UpdateTenantIdentityConfigurationRequest = {
      integrationBaseUrl: encodeURIComponent(formValue.integrationBaseUrl.trim()),
      clientId: formValue.clientId.trim(),
      clientSecret: formValue.clientSecret?.trim() ? formValue.clientSecret.trim() : null,
      azureAdTenantId: formValue.azureAdTenantId?.trim() ? formValue.azureAdTenantId.trim() : null,
      callbackEndpointPath: formValue.callbackEndpointPath?.trim() ? formValue.callbackEndpointPath.trim() : null
    };

    this.tenantIdentityService.updateConfiguration(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.statusMessage.set('Integration settings saved successfully.');
          this.errorMessage.set('');
          this.integrationForm.patchValue({ clientSecret: '' });
          this.hasClientSecret.set(response.isClientSecretConfigured);
          this.lastUpdatedAt.set(response.modifiedAtUtc ?? response.createdAtUtc ?? null);
          this.isSaving.set(false);
          this.integrationForm.markAsPristine();
        },
        error: (error: Error) => {
          this.errorMessage.set(error.message ?? 'Failed to save integration settings.');
          this.statusMessage.set('');
          this.isSaving.set(false);
        }
      });
  }

  reloadConfiguration(): void {
    this.loadConfiguration();
  }

  getControlError(controlName: string): string | null {
    const control = this.integrationForm.get(controlName);
    if (!control || !control.invalid || !control.touched) {
      return null;
    }

    if (control.errors?.['required']) {
      return 'This field is required.';
    }

    if (control.errors?.['pattern']) {
      if (controlName === 'integrationBaseUrl') {
        return 'URL must start with https://';
      }
      if (controlName === 'clientId' || controlName === 'azureAdTenantId') {
        return 'Must be a valid GUID.';
      }
      return 'Invalid format.';
    }

    return 'Invalid value.';
  }

  trackByIndex(index: number): number {
    return index;
  }

  private patchFormWithConfiguration(config: TenantIdentityConfigurationResponse): void {
    this.integrationForm.patchValue({
      integrationBaseUrl: config.integrationBaseUrl,
      clientId: config.clientId,
      azureAdTenantId: config.azureAdTenantId || '',
      callbackEndpointPath: config.callbackEndpointPath || ''
    });

    this.hasClientSecret.set(config.isClientSecretConfigured);
    this.lastUpdatedAt.set(config.modifiedAtUtc ?? config.createdAtUtc ?? null);
    this.statusMessage.set('Current configuration loaded.');
    this.errorMessage.set('');
    this.isLoading.set(false);
    this.integrationForm.markAsPristine();
  }
}
